import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { FormGroup, FormControl, ControlLabel } from 'react-bootstrap';
import Slider from 'react-rangeslider';

import Definition from '../definition';
import { convertToFloat, formatInputValue } from '../../utilities/formatting';

export default class CalculatorInput extends PureComponent {
    constructor(props) {
        super(props);
        const { defaultValue } = props;

        this.state = this.formatStateValue(defaultValue);
        this.initialValue = defaultValue;

        this.calculateAndFormat = this.calculateAndFormat.bind(this);
        this.handleInputChange = this.handleInputChange.bind(this);
        this.handleSliderChange = this.handleSliderChange.bind(this);
        this.handleInputBlur = this.handleInputBlur.bind(this);
        this.handleInputKeyUp = this.handleInputKeyUp.bind(this);
    }

    componentWillReceiveProps(nextProps) {
        if (this.props.defaultValue !== nextProps.defaultValue) {
            this.setState(this.formatStateValue(nextProps.defaultValue));
        }
    }

    getStep() {
        switch (this.props.format) {
        case 'currency':
            return this.initialValue / 10;
        case 'percent':
            return 0.1;
        case 'int':
            return 1;
        default:
            return 1;
        }
    }

    getMax() {
        switch (this.props.format) {
        case 'currency':
            return this.initialValue * 10;
        case 'percent':
            return 25;
        case 'int':
            return 100;
        default:
            return 100;
        }
    }

    formatStateValue(value) {
        return {
            value: formatInputValue(value, this.props.format)
        };
    }

    buildLabels(min, max) {
        const labels = {};

        [0, 0.25, 0.5, 0.75, 1].forEach((interval) => {
            const realInterval = (min + max) * interval;
            labels[realInterval] = formatInputValue(realInterval, this.props.format, true);
        });

        return labels;
    }

    calculateAndFormat() {
        const { processInput, id, controlledId } = this.props;
        const { value } = this.state;

        // we don't care about raw value here so just move it to process input fn
        const rawValue = convertToFloat(value);
        processInput(id, rawValue);

        if (id === 'zipCode') {
            // extract this logic out
            fetch(`${process.env.NODE_ENV === 'development' ? 'http://localhost:9000' : '/.netlify/functions'}/test?zipCode=${rawValue}&type=${controlledId === 'salesTax' ? 'salesTax' : 'propertyTax'}`).then(response => response.json()
            ).then((taxRate) => {
                // check that it's valid here. will need some validation as well
                processInput(controlledId, convertToFloat(taxRate) * 100);
            }).catch((error) => {
                console.error(error);
            });
        } else if (id === 'costOfAsset') {
            processInput('downPayment', rawValue * 0.2);
        }

        this.setState(this.formatStateValue(value));
    }

    handleInputChange(e) {
        this.setState({
            value: e.target.value
        });
    }

    handleSliderChange(value) {
        const { processInput, id } = this.props;

        const rawValue = convertToFloat(value);
        processInput(id, rawValue);

        this.setState(this.formatStateValue(value));

        if (id === 'costOfAsset') {
            processInput('downPayment', rawValue * 0.2);
        }
    }

    handleInputBlur() {
        this.calculateAndFormat();
    }

    handleInputKeyUp(ev) {
        if (ev.key === 'Enter') {
            this.calculateAndFormat();
        }
    }

    render() {
        const { id, label, showSlider, maxSliderValue, level } = this.props;
        const { value } = this.state;
        const min = 0;
        const max = maxSliderValue || this.getMax();
        const step = this.getStep();

        const level0primary = '#2693e6';
        const level0secondary = '#86d3ff';

        const level1primary = '#e66926';
        const level1secondary = '#eabca3';

        const level2primary = '#b026e6';
        const level2secondary = '#cda3de';

        const handleColor = level ? (level === 2 ? level2primary : level1primary) : level0primary;
        const sliderColor = level ? (level === 2 ? level2secondary : level1secondary) : level0secondary;

        return (
            <FormGroup controlId={id} bsSize="large">
                <ControlLabel>{label}</ControlLabel>
                <Definition id={id} term={label} />
                <div
                    css={{
                        display: 'flex',
                        '@media(max-width: 767px)': {
                            flexDirection: 'column',
                        },
                        '&& .rangeslider.rangeslider-horizontal': {
                            marginTop: 4,
                            width: '100%',
                            '@media(max-width: 767px)': {
                                marginBottom: 32,
                            },
                        },
                        '&& .form-control': {
                            width: 160,
                            marginRight: 40,
                            '@media(max-width: 767px)': {
                                width: '100%',
                                marginRight: 0,
                                marginBottom: 12,
                            },
                        },
                        '&& .rangeslider__fill': {
                            backgroundColor: sliderColor,
                            boxShadow: 'none',
                        },
                        '&& .rangeslider-horizontal': {
                            height: 18,
                            boxShadow: 'none',
                        },
                        '&& .rangeslider__handle': {
                            backgroundColor: handleColor,
                            height: 30,
                            width: 30,
                            boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.5)',
                            border: 'none',
                            outline: 'none',
                        },
                        '&& .rangeslider__handle:after': {
                            content: 'none',
                        }
                    }}
                >
                    <FormControl
                        type="text"
                        value={value}
                        onChange={this.handleInputChange}
                        onBlur={this.handleInputBlur}
                        onKeyUp={this.handleInputKeyUp}
                    />
                    {showSlider &&
                        <Slider
                            min={min}
                            max={max}
                            step={step}
                            value={convertToFloat(value)}
                            labels={this.buildLabels(min, max)}
                            tooltip={false}
                            onChange={this.handleSliderChange}
                        />
                    }
                </div>
            </FormGroup>
        );
    }
}

CalculatorInput.propTypes = {
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    defaultValue: PropTypes.number,
    processInput: PropTypes.func.isRequired,
    format: PropTypes.oneOf(['currency', 'percent', 'int']),
    showSlider: PropTypes.bool,
    maxSliderValue: PropTypes.number,
    controlledId: PropTypes.string,
};

CalculatorInput.defaultProps = {
    defaultValue: 0.00,
    format: 'currency',
    showSlider: true,
    maxSliderValue: null,
    controlledId: null,
};
